// Centralised editor/terminal theme registry used by Monaco, CodeMirror, and xterm.
// Each entry defines:
//   - monaco: Monaco `editor.defineTheme` token/color configuration
//   - codemirror: CSS colors consumed by the CM6 EditorView.theme in MobileEditorPane
//   - terminal: xterm theme object
//   - background: the single canonical background for everything (editor + terminal)
//   - accent: used for caret / line highlight

const THEMES = {
  brand: {
    label: "NoEffortsAi",
    background: "#0d1117",
    panelBackground: "#090c1a",
    monaco: {
      base: "vs-dark",
      tokens: [
        { token: "comment", foreground: "4a5580", fontStyle: "italic" },
        { token: "keyword", foreground: "c084fc" },
        { token: "string",  foreground: "6ee7b7" },
        { token: "number",  foreground: "fb923c" },
        { token: "type",    foreground: "67e8f9" },
        { token: "function",foreground: "a78bfa" },
      ],
      colors: {
        "editor.background": "#0d1117",
        "editor.foreground": "#e4eaff",
        "editor.lineHighlightBackground": "#161b22aa",
        "editorLineNumber.foreground": "#3d4857",
        "editorLineNumber.activeForeground": "#7c3aed",
        "editorCursor.foreground": "#7c3aed",
        "editor.selectionBackground": "#7c3aed44",
      },
    },
    codemirror: {
      fg: "#e4eaff", caret: "#7c3aed", selection: "#7c3aed55",
      gutterFg: "#3d4857", activeLineBg: "#161b22", activeGutterFg: "#7c3aed",
      comment: "#4a5580", keyword: "#c084fc", string: "#6ee7b7",
      number: "#fb923c", type: "#67e8f9", func: "#a78bfa",
      operator: "#94a3b8", bracket: "#22d3ee", tag: "#fb923c",
      invalid: "#f43f5e",
    },
    terminal: {
      background: "#090c1a", foreground: "#e4eaff",
      cursor: "#7c3aed", selectionBackground: "#7c3aed44",
      black: "#111830", red: "#f43f5e", green: "#34d399",
      yellow: "#f59e0b", blue: "#7c3aed", magenta: "#c084fc",
      cyan: "#22d3ee", white: "#e4eaff",
      brightBlack: "#2a3460", brightRed: "#fb7185",
      brightGreen: "#6ee7b7", brightYellow: "#fbbf24",
      brightBlue: "#a78bfa", brightMagenta: "#d8b4fe",
      brightCyan: "#67e8f9", brightWhite: "#ffffff",
    },
  },
  githubDark: {
    label: "GitHub Dark",
    background: "#0d1117",
    panelBackground: "#161b22",
    monaco: {
      base: "vs-dark",
      tokens: [
        { token: "comment", foreground: "8b949e", fontStyle: "italic" },
        { token: "keyword", foreground: "ff7b72" },
        { token: "string",  foreground: "a5d6ff" },
        { token: "number",  foreground: "79c0ff" },
        { token: "type",    foreground: "ffa657" },
        { token: "function",foreground: "d2a8ff" },
      ],
      colors: {
        "editor.background": "#0d1117",
        "editor.foreground": "#c9d1d9",
        "editor.lineHighlightBackground": "#161b2288",
        "editorLineNumber.foreground": "#484f58",
        "editorLineNumber.activeForeground": "#c9d1d9",
        "editorCursor.foreground": "#58a6ff",
        "editor.selectionBackground": "#264f7844",
      },
    },
    codemirror: {
      fg: "#c9d1d9", caret: "#58a6ff", selection: "#264f7855",
      gutterFg: "#484f58", activeLineBg: "#161b22", activeGutterFg: "#c9d1d9",
      comment: "#8b949e", keyword: "#ff7b72", string: "#a5d6ff",
      number: "#79c0ff", type: "#ffa657", func: "#d2a8ff",
      operator: "#ff7b72", bracket: "#c9d1d9", tag: "#7ee787",
      invalid: "#f85149",
    },
    terminal: {
      background: "#010409", foreground: "#c9d1d9",
      cursor: "#58a6ff", selectionBackground: "#264f7855",
      black: "#484f58", red: "#ff7b72", green: "#7ee787",
      yellow: "#d29922", blue: "#58a6ff", magenta: "#bc8cff",
      cyan: "#39c5cf", white: "#b1bac4",
      brightBlack: "#6e7681", brightRed: "#ffa198",
      brightGreen: "#56d364", brightYellow: "#e3b341",
      brightBlue: "#79c0ff", brightMagenta: "#d2a8ff",
      brightCyan: "#56d4dd", brightWhite: "#f0f6fc",
    },
  },
  dracula: {
    label: "Dracula",
    background: "#282a36",
    panelBackground: "#21222c",
    monaco: {
      base: "vs-dark",
      tokens: [
        { token: "comment", foreground: "6272a4", fontStyle: "italic" },
        { token: "keyword", foreground: "ff79c6" },
        { token: "string",  foreground: "f1fa8c" },
        { token: "number",  foreground: "bd93f9" },
        { token: "type",    foreground: "8be9fd" },
        { token: "function",foreground: "50fa7b" },
      ],
      colors: {
        "editor.background": "#282a36",
        "editor.foreground": "#f8f8f2",
        "editor.lineHighlightBackground": "#44475a55",
        "editorLineNumber.foreground": "#6272a4",
        "editorLineNumber.activeForeground": "#f8f8f2",
        "editorCursor.foreground": "#f8f8f0",
        "editor.selectionBackground": "#44475a",
      },
    },
    codemirror: {
      fg: "#f8f8f2", caret: "#f8f8f0", selection: "#44475a99",
      gutterFg: "#6272a4", activeLineBg: "#44475a55", activeGutterFg: "#f8f8f2",
      comment: "#6272a4", keyword: "#ff79c6", string: "#f1fa8c",
      number: "#bd93f9", type: "#8be9fd", func: "#50fa7b",
      operator: "#ff79c6", bracket: "#f8f8f2", tag: "#ff79c6",
      invalid: "#ff5555",
    },
    terminal: {
      background: "#21222c", foreground: "#f8f8f2",
      cursor: "#f8f8f0", selectionBackground: "#44475a",
      black: "#21222c", red: "#ff5555", green: "#50fa7b",
      yellow: "#f1fa8c", blue: "#bd93f9", magenta: "#ff79c6",
      cyan: "#8be9fd", white: "#f8f8f2",
      brightBlack: "#6272a4", brightRed: "#ff6e6e",
      brightGreen: "#69ff94", brightYellow: "#ffffa5",
      brightBlue: "#d6acff", brightMagenta: "#ff92df",
      brightCyan: "#a4ffff", brightWhite: "#ffffff",
    },
  },
  tokyoNight: {
    label: "Tokyo Night",
    background: "#1a1b26",
    panelBackground: "#16161e",
    monaco: {
      base: "vs-dark",
      tokens: [
        { token: "comment", foreground: "565f89", fontStyle: "italic" },
        { token: "keyword", foreground: "bb9af7" },
        { token: "string",  foreground: "9ece6a" },
        { token: "number",  foreground: "ff9e64" },
        { token: "type",    foreground: "2ac3de" },
        { token: "function",foreground: "7aa2f7" },
      ],
      colors: {
        "editor.background": "#1a1b26",
        "editor.foreground": "#c0caf5",
        "editor.lineHighlightBackground": "#24283b88",
        "editorLineNumber.foreground": "#3b4261",
        "editorLineNumber.activeForeground": "#7aa2f7",
        "editorCursor.foreground": "#c0caf5",
        "editor.selectionBackground": "#2e3c6488",
      },
    },
    codemirror: {
      fg: "#c0caf5", caret: "#c0caf5", selection: "#2e3c6477",
      gutterFg: "#3b4261", activeLineBg: "#24283b88", activeGutterFg: "#7aa2f7",
      comment: "#565f89", keyword: "#bb9af7", string: "#9ece6a",
      number: "#ff9e64", type: "#2ac3de", func: "#7aa2f7",
      operator: "#89ddff", bracket: "#c0caf5", tag: "#f7768e",
      invalid: "#f7768e",
    },
    terminal: {
      background: "#16161e", foreground: "#c0caf5",
      cursor: "#c0caf5", selectionBackground: "#2e3c6477",
      black: "#15161e", red: "#f7768e", green: "#9ece6a",
      yellow: "#e0af68", blue: "#7aa2f7", magenta: "#bb9af7",
      cyan: "#7dcfff", white: "#a9b1d6",
      brightBlack: "#414868", brightRed: "#f7768e",
      brightGreen: "#9ece6a", brightYellow: "#e0af68",
      brightBlue: "#7aa2f7", brightMagenta: "#bb9af7",
      brightCyan: "#7dcfff", brightWhite: "#c0caf5",
    },
  },
  monokai: {
    label: "Monokai",
    background: "#272822",
    panelBackground: "#1e1f1c",
    monaco: {
      base: "vs-dark",
      tokens: [
        { token: "comment", foreground: "75715e", fontStyle: "italic" },
        { token: "keyword", foreground: "f92672" },
        { token: "string",  foreground: "e6db74" },
        { token: "number",  foreground: "ae81ff" },
        { token: "type",    foreground: "66d9ef" },
        { token: "function",foreground: "a6e22e" },
      ],
      colors: {
        "editor.background": "#272822",
        "editor.foreground": "#f8f8f2",
        "editor.lineHighlightBackground": "#3e3d3288",
        "editorLineNumber.foreground": "#75715e",
        "editorLineNumber.activeForeground": "#f8f8f2",
        "editorCursor.foreground": "#f8f8f0",
        "editor.selectionBackground": "#49483e",
      },
    },
    codemirror: {
      fg: "#f8f8f2", caret: "#f8f8f0", selection: "#49483e99",
      gutterFg: "#75715e", activeLineBg: "#3e3d3288", activeGutterFg: "#f8f8f2",
      comment: "#75715e", keyword: "#f92672", string: "#e6db74",
      number: "#ae81ff", type: "#66d9ef", func: "#a6e22e",
      operator: "#f92672", bracket: "#f8f8f2", tag: "#f92672",
      invalid: "#f92672",
    },
    terminal: {
      background: "#1e1f1c", foreground: "#f8f8f2",
      cursor: "#f8f8f0", selectionBackground: "#49483e",
      black: "#272822", red: "#f92672", green: "#a6e22e",
      yellow: "#f4bf75", blue: "#66d9ef", magenta: "#ae81ff",
      cyan: "#a1efe4", white: "#f8f8f2",
      brightBlack: "#75715e", brightRed: "#f92672",
      brightGreen: "#a6e22e", brightYellow: "#f4bf75",
      brightBlue: "#66d9ef", brightMagenta: "#ae81ff",
      brightCyan: "#a1efe4", brightWhite: "#f9f8f5",
    },
  },
};

export const THEME_LIST = Object.keys(THEMES).map((id) => ({ id, label: THEMES[id].label }));

export function getTheme(id) {
  return THEMES[id] || THEMES.brand;
}

export const DEFAULT_THEME_ID = "brand";

export const THEME_LS_KEY = "runbox:editor-theme";

export function readStoredTheme() {
  try {
    const v = localStorage.getItem(THEME_LS_KEY);
    if (v && THEMES[v]) return v;
  } catch (_) { /* */ }
  return DEFAULT_THEME_ID;
}

export function writeStoredTheme(id) {
  try { localStorage.setItem(THEME_LS_KEY, id); } catch (_) { /* */ }
}
